<template>
  <section class="oms-filter-board">
    <div
      v-if="showHeading && hasHeadingContent"
      class="oms-filter-board__heading"
      :class="{ 'oms-filter-board__heading--actions-only': !hasTitleContent }"
    >
      <div v-if="hasTitleContent" class="oms-filter-board__title">
        <span v-if="eyebrow">{{ eyebrow }}</span>
        <h2 v-if="title">{{ title }}</h2>
      </div>
      <slot name="header-actions" />
    </div>
    <div class="oms-filter-board__grid">
      <slot />
    </div>
  </section>
</template>

<script>
export default {
  name: "FilterBoard",
  props: {
    eyebrow: {
      type: String,
      default: "Filter Board",
    },
    showHeading: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
  },
  computed: {
    hasTitleContent() {
      return Boolean(this.eyebrow || this.title);
    },
    hasHeaderActions() {
      return Boolean(
        this.$slots["header-actions"] || this.$scopedSlots["header-actions"]
      );
    },
    hasHeadingContent() {
      return this.hasTitleContent || this.hasHeaderActions;
    },
  },
};
</script>

<style scoped>
.oms-filter-board {
  background: var(--oms-surface-soft, #faf9f6);
  border: 0.0625rem solid var(--oms-border, #e4e1d9);
  border-radius: 0.875rem;
  box-sizing: border-box;
  max-width: none;
  overflow: visible;
  padding: var(--oms-filter-board-padding, 0.75rem 0.875rem) !important;
}

.oms-filter-board__heading {
  align-items: flex-end;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.oms-filter-board__heading--actions-only {
  justify-content: flex-end;
}

.oms-filter-board__title {
  min-width: 0;
}

.oms-filter-board__title span {
  color: var(--oms-primary-dark, #d96a10);
  display: block;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: uppercase;
}

.oms-filter-board__title h2 {
  color: var(--oms-ink-strong, #1a1916);
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0.25rem 0 0;
  overflow-wrap: anywhere;
  text-transform: capitalize;
}

.oms-filter-board__grid {
  align-items: start;
  display: grid;
  gap: var(--oms-filter-board-gap, 0.875rem);
  grid-auto-flow: row dense;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  margin: 0 !important;
  min-width: 0;
  padding: 0 !important;
  width: 100%;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__field,
.oms-filter-board__grid ::v-deep .flex.oms-filter-board__field {
  align-items: stretch;
  align-self: start;
  box-sizing: border-box;
  display: flex !important;
  flex: none !important;
  height: auto !important;
  max-width: none !important;
  min-height: var(--oms-filter-control-height, 2.5rem);
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__field--search {
  grid-column: span 2;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__field--date {
  min-width: 13rem;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__field--wide {
  grid-column: span 2;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__field--tight {
  max-width: 9rem !important;
}

.oms-filter-board__grid ::v-deep .oms-filter-board__actions,
.oms-filter-board__grid ::v-deep .flex.oms-filter-board__actions {
  align-items: stretch;
  align-self: start;
  box-sizing: border-box;
  display: flex !important;
  flex: none !important;
  height: auto !important;
  justify-content: stretch;
  margin-left: 0;
  max-width: none !important;
  min-height: var(--oms-filter-control-height, 2.5rem);
  min-width: 7rem;
  padding: 0 !important;
  width: 100% !important;
}

.oms-filter-board ::v-deep .divider-style {
  display: none !important;
}

.oms-filter-board ::v-deep .v-card__actions {
  align-items: stretch;
  display: flex;
  padding: 0 !important;
  width: 100%;
}

.oms-filter-board ::v-deep .v-input,
.oms-filter-board ::v-deep .v-text-field,
.oms-filter-board ::v-deep .v-select,
.oms-filter-board ::v-deep .v-autocomplete,
.oms-filter-board ::v-deep .wrapper,
.oms-filter-board ::v-deep .e-input-group,
.oms-filter-board ::v-deep .e-control-wrapper {
  display: flex;
  height: var(--oms-filter-control-height, 2.5rem) !important;
  margin: 0 !important;
  max-width: 100% !important;
  min-height: var(--oms-filter-control-height, 2.5rem) !important;
  padding: 0 !important;
  width: 100% !important;
}

.oms-filter-board ::v-deep .v-input__control,
.oms-filter-board ::v-deep .v-input__slot,
.oms-filter-board ::v-deep .v-select__slot,
.oms-filter-board ::v-deep .v-text-field__slot {
  min-height: var(--oms-filter-control-height, 2.5rem) !important;
  width: 100%;
}

.oms-filter-board ::v-deep .v-input__slot,
.oms-filter-board ::v-deep .e-input-group,
.oms-filter-board ::v-deep .e-control-wrapper {
  align-items: center !important;
  background: var(--oms-surface-soft, #fbfaf7) !important;
  border: 0.0625rem solid var(--oms-border, #e4e1d9) !important;
  border-radius: 0.5rem !important;
  box-shadow: inset 0 0.0625rem 0 rgba(26, 25, 22, 0.03) !important;
  box-sizing: border-box;
  display: flex !important;
  height: var(--oms-filter-control-height, 2.5rem) !important;
  margin: 0 !important;
  min-height: var(--oms-filter-control-height, 2.5rem) !important;
  padding: 0 0.8125rem !important;
  width: 100%;
}

/* The fields are boxed, so Vuetify's underline would draw a second, darker
   bottom edge on some of them. */
.oms-filter-board ::v-deep .v-input__slot::before,
.oms-filter-board ::v-deep .v-input__slot::after {
  display: none !important;
}

/* With the underline gone, focus shows on the box itself. */
.oms-filter-board ::v-deep .v-input--is-focused .v-input__slot {
  border-color: var(--oms-primary, #ea7a1c) !important;
}

.oms-filter-board
  ::v-deep
  .v-text-field--outline
  > .v-input__control
  > .v-input__slot {
  border-color: var(--oms-border, #e4e1d9) !important;
  border-width: 0.0625rem !important;
}

.oms-filter-board ::v-deep .v-text-field input,
.oms-filter-board ::v-deep .v-select input,
.oms-filter-board ::v-deep .e-input,
.oms-filter-board ::v-deep input {
  color: var(--oms-ink, #3a382f) !important;
  font-size: 0.875rem !important;
  font-weight: 500;
  height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  line-height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  margin: 0 !important;
  max-height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  min-height: 0 !important;
  overflow: hidden;
  padding: 0 !important;
  text-overflow: ellipsis;
}

.oms-filter-board ::v-deep .v-label,
.oms-filter-board ::v-deep .v-select__selection,
.oms-filter-board ::v-deep .e-input::placeholder {
  color: var(--oms-muted, #74716a) !important;
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0;
}

.oms-filter-board ::v-deep .v-label {
  top: 0.625rem !important;
}

.oms-filter-board ::v-deep .v-label--active {
  transform: translateY(-1.125rem) scale(0.75) !important;
}

.oms-filter-board ::v-deep .v-select__selections {
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.25rem;
  min-height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  overflow: hidden;
  padding: 0 !important;
}

.oms-filter-board ::v-deep .v-select.v-select--chips .v-select__selections {
  max-height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  overflow: hidden !important;
}

.oms-filter-board ::v-deep .v-input__append-inner,
.oms-filter-board ::v-deep .v-input__append-outer,
.oms-filter-board ::v-deep .v-input__prepend-inner,
.oms-filter-board ::v-deep .v-input__prepend-outer {
  align-self: center;
  margin-top: 0 !important;
}

.oms-filter-board ::v-deep .v-input__icon {
  align-items: center;
  display: inline-flex;
  height: 1.5rem;
  justify-content: center;
  min-width: 1.5rem;
}

.oms-filter-board ::v-deep .v-icon {
  color: var(--oms-muted, #74716a);
  font-size: 1.125rem !important;
  line-height: 1;
}

.oms-filter-board ::v-deep .v-text-field__details {
  display: none !important;
}

.oms-filter-board ::v-deep .e-input-group {
  position: relative;
}

.oms-filter-board ::v-deep .e-input-group-icon,
.oms-filter-board ::v-deep .e-ddl-icon,
.oms-filter-board ::v-deep .e-range-icon {
  align-items: center;
  display: inline-flex !important;
  height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  justify-content: center;
  margin: 0 !important;
  min-height: var(--oms-filter-control-inner-height, 2.375rem) !important;
  position: static !important;
}

.oms-filter-board ::v-deep .e-multi-select-wrapper {
  align-items: center;
  display: flex;
  min-height: var(--oms-filter-control-inner-height, 2.375rem);
  overflow: hidden;
  width: 100%;
}

.oms-filter-board ::v-deep .e-multi-select-wrapper .e-chips-collection {
  max-height: 2rem;
  overflow: hidden;
}

.oms-filter-board ::v-deep .oms-filter-board__reset,
.oms-filter-board ::v-deep .v-btn.oms-filter-board__reset {
  align-items: center !important;
  background: var(--oms-surface, #ffffff) !important;
  border: 0.0625rem solid var(--oms-border, #e4e1d9) !important;
  border-radius: 0.5rem !important;
  box-shadow: none !important;
  color: var(--oms-primary-dark, #d96a10) !important;
  display: inline-flex !important;
  font-size: 0.8125rem !important;
  font-weight: 800 !important;
  gap: 0.375rem;
  height: var(--oms-filter-control-height, 2.5rem) !important;
  justify-content: center !important;
  letter-spacing: 0 !important;
  line-height: 1.2;
  margin: 0 !important;
  min-height: var(--oms-filter-control-height, 2.5rem) !important;
  min-width: 5.5rem !important;
  padding: 0 0.75rem !important;
  text-transform: none !important;
  white-space: nowrap !important;
  width: 100%;
}

@media (max-width: 80rem) {
  .oms-filter-board__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .oms-filter-board__grid ::v-deep .oms-filter-board__field,
  .oms-filter-board__grid ::v-deep .flex.oms-filter-board__field,
  .oms-filter-board__grid ::v-deep .oms-filter-board__actions,
  .oms-filter-board__grid ::v-deep .flex.oms-filter-board__actions {
    margin-left: 0;
    min-width: 0;
    width: 100% !important;
  }
}

@media (max-width: 48rem) {
  .oms-filter-board {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  .oms-filter-board__heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .oms-filter-board__grid ::v-deep .oms-filter-board__field,
  .oms-filter-board__grid ::v-deep .flex.oms-filter-board__field,
  .oms-filter-board__grid ::v-deep .oms-filter-board__actions,
  .oms-filter-board__grid ::v-deep .flex.oms-filter-board__actions {
    grid-column: 1 / -1;
  }
}
</style>
